import { TypedDocumentNode, gql } from '@apollo/client';
import { useCallback } from 'react';

import { useWalletContext } from 'contexts/wallet';
import { isType } from 'lib/gql';

import type { useGetAuthorizationApprovals_authorizationRequest } from './__generated__/useGetAuthorizationApprovals.graphql';

export const useGetAuthorizationApprovals = () => {
  const { approveAuthorizationRequests } = useWalletContext();

  return useCallback(
    async (
      authorizations: useGetAuthorizationApprovals_authorizationRequest[]
    ) => {
      const walletAuthorizationRequests = authorizations
        .map(({ fingerprint, request }) =>
          isType(request, 'StripeCreditCardAuthorizationRequest') ||
          isType(request, 'MangopayCreditCardAuthorizationRequest') ||
          isType(request, 'CheckoutCreditCardAuthorizationRequest') ||
          isType(request, 'PaypalAuthorizationRequest') ||
          isType(request, 'MangopayKlarnaAuthorizationRequest') ||
          isType(request, 'MangopayApplePayAuthorizationRequest') ||
          isType(request, 'StarkexV6TokenTransferAuthorizationRequest')
            ? null
            : {
                ...request,
                fingerprint,
              }
        )
        .filter(Boolean)
        .map(request => {
          if (isType(request, 'EthereumBankTransferAuthorizationRequest')) {
            return {
              ...request,
              amount: request.ethereumBankTransferAmount,
              feeAmount: request.ethereumBankTransferFeeAmount,
            };
          }
          if (isType(request, 'StarkexTransferAuthorizationRequest')) {
            return { ...request, amount: request.weiAmount };
          }
          if (isType(request, 'BaseWithdrawalAuthorizationRequest')) {
            return {
              ...request,
              amount: request.baseWithdrawalAmount,
              feeAmount: request.baseWithdrawalFeeAmount,
            };
          }
          if (isType(request, 'SolanaTokenTransferAuthorizationRequest')) {
            return {
              ...request,
              senderAddress: request.solanaSenderAddress,
              receiverAddress: request.solanaReceiverAddress,
              nonce: request.solanaTokenTransferApprovalNonce,
              expirationTimestamp:
                request.solanaTokenTransferApprovalExpirationTimestamp,
            };
          }
          if (isType(request, 'SolanaBankTransferAuthorizationRequest')) {
            return {
              fingerprint: request.fingerprint,
              __typename: request.__typename,
              senderAddress: request.solanaBankTransferSenderAddress,
              receiverAddress: request.solanaBankTransferReceiverAddress,
              amount: request.solanaBankTransferAmount,
              feeAmount: request.solanaBankTransferFeeAmount,
              nonce: request.solanaBankTransferNonce,
              expirationTimestamp:
                request.solanaBankTransferExpirationTimestamp,
              originator: request.solanaBankTransferOriginator,
              bankProgramAddress: request.solanaBankTransferBankProgramAddress,
            };
          }
          if (isType(request, 'SolanaBankWithdrawalAuthorizationRequest')) {
            return {
              fingerprint: request.fingerprint,
              __typename: request.__typename,
              senderAddress: request.solanaBankWithdrawalSenderAddress,
              receiverAddress: request.solanaBankWithdrawalReceiverAddress,
              amount: request.solanaBankWithdrawalAmount,
              feeAmount: request.solanaBankWithdrawalFeeAmount,
              nonce: request.solanaBankWithdrawalNonce,
              expirationTimestamp:
                request.solanaBankWithdrawalExpirationTimestamp,
              originator: request.solanaBankWithdrawalOriginator,
              bankProgramAddress:
                request.solanaBankWithdrawalBankProgramAddress,
            };
          }
          return request;
        });
      return approveAuthorizationRequests(walletAuthorizationRequests);
    },
    [approveAuthorizationRequests]
  );
};

export const useGetAuthorizationApproval = () => {
  const getAuthorizationApprovals = useGetAuthorizationApprovals();

  return useCallback(
    async (
      authorization: useGetAuthorizationApprovals_authorizationRequest
    ) => {
      const { authorizationApprovals } = await getAuthorizationApprovals([
        authorization,
      ]);
      return authorizationApprovals?.[0];
    },
    [getAuthorizationApprovals]
  );
};

useGetAuthorizationApprovals.fragments = {
  authorizationRequest: gql`
    fragment useGetAuthorizationApprovals_authorizationRequest on AuthorizationRequest {
      id
      fingerprint
      request {
        ... on StarkexLimitOrderAuthorizationRequest {
          vaultIdSell
          vaultIdBuy
          amountSell
          amountBuy
          tokenSell
          tokenBuy
          nonce
          expirationTimestamp
          feeInfo {
            feeLimit
            sourceVaultId
            tokenId
          }
        }
        ... on StarkexTransferAuthorizationRequest {
          weiAmount: amount
          condition
          expirationTimestamp
          feeInfoUser {
            feeLimit
            sourceVaultId
            tokenId
          }
          nonce
          receiverPublicKey
          receiverVaultId
          senderVaultId
          token
        }
        ... on SolanaTokenTransferAuthorizationRequest {
          solanaSenderAddress: senderAddress
          solanaReceiverAddress: receiverAddress
          assetId
          solanaTokenTransferApprovalNonce: nonce
          solanaTokenTransferApprovalExpirationTimestamp: expirationTimestamp
          leafIndex
          merkleTreeAddress
          transferProxyProgramAddress
          originator
        }
        ... on SolanaBankTransferAuthorizationRequest {
          solanaBankTransferSenderAddress: senderAddress
          solanaBankTransferReceiverAddress: receiverAddress
          solanaBankTransferAmount: amount
          solanaBankTransferFeeAmount: feeAmount
          solanaBankTransferNonce: nonce
          solanaBankTransferExpirationTimestamp: expirationTimestamp
          solanaBankTransferOriginator: originator
          solanaBankTransferBankProgramAddress: bankProgramAddress
        }
        ... on SolanaBankWithdrawalAuthorizationRequest {
          solanaBankWithdrawalSenderAddress: senderAddress
          solanaBankWithdrawalReceiverAddress: receiverAddress
          solanaBankWithdrawalAmount: amount
          solanaBankWithdrawalFeeAmount: feeAmount
          solanaBankWithdrawalNonce: nonce
          solanaBankWithdrawalExpirationTimestamp: expirationTimestamp
          solanaBankWithdrawalOriginator: originator
          solanaBankWithdrawalBankProgramAddress: bankProgramAddress
        }
        ... on EthereumBankTransferAuthorizationRequest {
          senderAddress
          receiverAddress
          ethereumBankTransferAmount: amount
          ethereumBankTransferFeeAmount: feeAmount
          deadline
          salt
          proxyAddress
          contractAddress
        }
        ... on BaseWithdrawalAuthorizationRequest {
          senderAddress
          receiverAddress
          destinationAddress
          baseWithdrawalAmount: amount
          baseWithdrawalFeeAmount: feeAmount
          deadline
          salt
          proxyAddress
          contractAddress
        }
        ... on MangopayWalletTransferAuthorizationRequest {
          nonce
          amount
          currency
          operationHash
          mangopayWalletId
        }
        ... on StripeCreditCardAuthorizationRequest {
          id
          clientSecret
          amount
          paymentMethod
        }
        ... on MangopayCreditCardAuthorizationRequest {
          id
        }
        ... on PaypalAuthorizationRequest {
          id
          redirectUrl
        }
        ... on MangopayKlarnaAuthorizationRequest {
          id
          redirectUrl
        }
        ... on MangopayApplePayAuthorizationRequest {
          id
        }
      }
    }
  ` as TypedDocumentNode<useGetAuthorizationApprovals_authorizationRequest>,
};

useGetAuthorizationApproval.fragments = useGetAuthorizationApprovals.fragments;
